import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * DemoVideoModal — responsive YouTube embed lightbox for the Landing page.
 *
 * Behaviour:
 *   • Opens as a centered modal with a black backdrop (72 % opacity).
 *   • ESC key closes.
 *   • Click outside the video card closes.
 *   • Close (X) button closes.
 *   • Background scroll is locked while open.
 *   • Iframe `src` is set to '' on close → force the YouTube player to stop
 *     playback (react to state change, not to unmount, so the modal supports
 *     open→close→open in the same session).
 *   • Fullscreen is allowed via `allowFullScreen`.
 */
export default function DemoVideoModal({ open, onClose, videoId = "R1zJqvfzUpY", title = "HisaabPro Demo" }) {
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  // Query params:
  //   autoplay=1  → start immediately
  //   rel=0       → don't show unrelated videos in the "up next" grid
  //   modestbranding=1 → hide the big YouTube logo
  //   playsinline=1    → keep inline on iOS Safari (respects our modal)
  //   origin=<host>    → recommended by YouTube for embedded playback (auth)
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1${origin ? `&origin=${encodeURIComponent(origin)}` : ""}`;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm grid place-items-center p-3 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="landing-demo-modal"
    >
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white text-slate-900 shadow-lg grid place-items-center hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          aria-label="Close demo"
          data-testid="landing-demo-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
          {/* 16:9 responsive frame */}
          <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe
              src={src}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="landing-demo-iframe"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
